import React, { useState, useEffect } from 'react';

interface EmailInputProps {
  label: string;
  name: string;
  value: string;
  placeholder?: string;
  tabIndex?: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onError: (hasError: boolean) => void; // New prop to pass error state
}

const EmailInput: React.FC<EmailInputProps> = ({ label, name, value, placeholder, tabIndex, onChange, onError }) => {
  const [error, setError] = useState<string | null>(null);
  const [debouncedValue, setDebouncedValue] = useState(value);

  const validateEmail = (email: string): boolean => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 500); // Adjust the delay as needed

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  useEffect(() => {
    if (debouncedValue === '') {
      setError(null); // No error for blank input
      onError(false); // No error
    } else if (!validateEmail(debouncedValue)) {
      setError('Invalid email format'.toTranslatedDisplay()); // Show error for invalid email
      onError(true); // Trigger error
    } else {
      setError(null); // No error for valid email
      onError(false); // No error
    }
  }, [debouncedValue, onError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
  };

  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <div className="input-group">
        <div className="input-group-prepend">
          <span className="rounded-0 rounded-start input-group-text">
            <i className="bi-envelope-fill email-icon"></i>
          </span>
        </div>
        <input
          type="email"
          className={`form-control no-spinner ${error ? 'is-invalid' : ''}`}
          placeholder={placeholder || label}
          name={name}
          value={value}
          onChange={handleChange}
          tabIndex={tabIndex}
        />
      </div>
      {error && <div className="text-danger">{error}</div>}
    </div>
  );
};

export default EmailInput;
